import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { Modal } from "antd";
import { RiFireFill } from "react-icons/ri";

// Loader component
const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const CAL_GOAL = 2000; // Daily calorie goal

const CaloriesData = ({ smartWatchId }) => {
  const [dailies, setDailies] = useState({});
  const patJson = localStorage.getItem("patientData");
  const patObj = patJson ? JSON.parse(patJson) : null;
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (smartWatchId) fetchDailyCalories();
  }, [smartWatchId]);

  const fetchDailyCalories = async () => {
    const today = dayjs().format("DD/MM/YYYY");
    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: today,
        toDate: today,
        type: "DAILIES",
      });

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        setDailies(raw);
      } else {
        setDailies({});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  const showGraph = () => {
    setIsModalOpen(true);
  };

  const active = dailies?.activeKilocalories ?? 0;
  const resting = dailies?.bmrKilocalories ?? 0;
  const totalCalories = active + resting;

  return (
    <div onClick={showGraph} className="cursor-pointer">
      {loading && <Loader />}
      <div className="bg-white shadow rounded-lg p-4 w-full h-40 hover:shadow-lg transition">
        <div className="flex items-center gap-2 text-sm font-sans font-semibold mb-4">
          <RiFireFill className="text-green-500 w-5 h-5" />
          <span>Calories Burned</span>
        </div>
        <div className="text-lg font-semibold mb-4">{totalCalories}</div>
        <div className="w-full h-3 bg-gray-200 rounded-full flex overflow-hidden mb-2">
          <div
            className="bg-red-500"
            style={{ width: `${(active / totalCalories) * 100}%` }}
          ></div>
          <div
            className="bg-blue-500"
            style={{ width: `${(resting / totalCalories) * 100}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <div className="flex flex-col items-start">
            <span className="font-semibold">{active}</span>
            <span className="text-gray-500 text-xs">Active</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="font-semibold">{resting}</span>
            <span className="text-gray-500 text-xs">Resting</span>
          </div>
        </div>
      </div>

      {smartWatchId && (
        <CaloriesModal
          open={isModalOpen}
          onClose={handleClose}
          smartWatchId={smartWatchId}
        />
      )}
    </div>
  );
};

const CaloriesModal = ({ open, onClose, smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const fetch1DData = async (date) => {
    try {
      setLoading(true);
      const today = date.format("DD/MM/YYYY");
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: today,
        toDate: today,
        type: "DAILIES",
      });
      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        setChartData([
          {
            date: raw.calendarDate,
            active: raw.activeKilocalories ?? 0,
            resting: raw.bmrKilocalories ?? 0,
            total: (raw.activeKilocalories ?? 0) + (raw.bmrKilocalories ?? 0),
          },
        ]);
      } else setChartData([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetch7DData = async () => {
    const from = selectedDate.subtract(6, "day").format("DD/MM/YYYY");
    const to = selectedDate.format("DD/MM/YYYY");
    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: from,
        toDate: to,
        type: "DAILIES",
      });
      if (res?.data?.length > 0) {
        const parsed = res.data.map((item) => {
          const raw = JSON.parse(item.RAW_JSON || "{}");
          return {
            date: raw.calendarDate,
            active: raw.activeKilocalories ?? 0,
            resting: raw.bmrKilocalories ?? 0,
            total: (raw.activeKilocalories ?? 0) + (raw.bmrKilocalories ?? 0),
          };
        });
        parsed.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
        setChartData(parsed);
      } else setChartData([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") fetch1DData(selectedDate);
    else fetch7DData();
  }, [tab, selectedDate, smartWatchId]);

  const handlePrevDay = () => {
    const newDate = selectedDate.subtract(1, "day");
    setSelectedDate(newDate);
  };
  const handleNextDay = () => {
    const newDate = selectedDate.add(1, "day");
    if (newDate.isAfter(dayjs(), "day")) return;
    setSelectedDate(newDate);
  };

  const avgTotal =
    chartData.reduce((sum, item) => sum + (item.total ?? 0), 0) /
    (chartData.length || 1);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title="Calories Burned Summary"
    >
      {loading && <Loader />}

      {/* Tabs */}
      <div className="flex justify-center mb-4 gap-3">
        <button
          onClick={() => setTab("1D")}
          className={`${
            tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
          } px-4 py-2 rounded-md`}
        >
          1D
        </button>
        <button
          className={`${
            tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
          } px-4 py-2 rounded-md`}
          onClick={() => setTab("7D")}
        >
          7D
        </button>
      </div>

      {/* 1D navigation */}
      {tab === "1D" && (
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            className="px-4 py-2 rounded-md text-white bg-brand-primaryColor"
            onClick={handlePrevDay}
          >
            ← Prev Day
          </button>
          <span className="font-semibold">
            {selectedDate.format("DD/MM/YYYY")}
          </span>
          <button
            className="px-4 py-2 rounded-md text-white bg-brand-primaryColor"
            onClick={handleNextDay}
          >
            Next Day →
          </button>
        </div>
      )}

      {tab === "7D" && (
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            className="px-4 py-2 rounded-md text-white bg-brand-primaryColor"
            onClick={() => {
              const newDate = selectedDate.subtract(7, "day");
              setSelectedDate(newDate);
            }}
          >
            ← Prev 7 Days
          </button>

          <span className="font-semibold">
            {selectedDate.subtract(6, "day").format("DD/MM/YYYY")} -{" "}
            {selectedDate.format("DD/MM/YYYY")}
          </span>

          <button
            className="px-4 py-2 rounded-md text-white bg-brand-primaryColor"
            onClick={() => {
              const newDate = selectedDate.add(7, "day");
              if (newDate.isAfter(dayjs(), "day")) return; // prevent future
              setSelectedDate(newDate);
            }}
          >
            Next 7 Days →
          </button>
        </div>
      )}

      {/* 1D Circular Chart */}
      {tab === "1D" && chartData[0] && (
        <div className="flex flex-col items-center mt-6">
          <div className="relative w-36 h-36 mb-4">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <path
                className="stroke-gray-200"
                strokeWidth="4"
                fill="none"
                d="M18 2.0845 a15.9155 15.9155 0 1 1 0 31.831 a15.9155 15.9155 0 1 1 0-31.831"
              />
              <path
                stroke={chartData[0].total >= CAL_GOAL ? "#22c55e" : "#ff6384"}
                strokeWidth="4"
                strokeDasharray={`${
                  (chartData[0].total / CAL_GOAL) * 100
                }, 100`}
                strokeLinecap="round"
                fill="none"
                d="M18 2.0845 a15.9155 15.9155 0 1 1 0 31.831 a15.9155 15.9155 0 1 1 0-31.831"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-semibold">
                {chartData[0].total}
              </span>
              <span className="text-xs text-gray-500">kcal</span>
            </div>
          </div>

          {/* Active / Resting Labels */}
          <div className="grid grid-cols-2 gap-4 text-center text-sm">
            <div>
              <p className="text-red-500 font-semibold">
                {chartData[0].active}
              </p>
              <p className="text-gray-500 text-xs">Active</p>
            </div>
            <div>
              <p className="text-blue-500 font-semibold">
                {chartData[0].resting}
              </p>
              <p className="text-gray-500 text-xs">Resting</p>
            </div>
          </div>
        </div>
      )}

      {/* 7D Chart */}
      {tab === "7D" && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData.map((item) => ({ ...item, avg: item.total / 1 }))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => dayjs(d).format("DD/MM")}
              />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "resting")
                    return [`${value}`, "Resting Calories"];
                  if (name === "active") return [`${value}`, "Active Calories"];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar
                dataKey="resting"
                stackId="a"
                fill="#36a2eb"
                name="Resting Calories"
              />
              <Bar
                dataKey="active"
                stackId="a"
                fill="#ff6384"
                name="Active Calories"
              />
            </BarChart>
          </ResponsiveContainer>

          <div className="flex justify-around mt-5 text-center">
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {(
                  chartData.reduce((sum, day) => sum + (day.active ?? 0), 0) /
                  (chartData.length || 1)
                ).toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Active</p>
            </div>

            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {(
                  chartData.reduce((sum, day) => sum + (day.resting ?? 0), 0) /
                  (chartData.length || 1)
                ).toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Resting</p>
            </div>

            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {(
                  chartData.reduce((sum, day) => sum + (day.total ?? 0), 0) /
                  (chartData.length || 1)
                ).toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Total</p>
            </div>
          </div>

          {/* 7D Day Cards */}
          <div className="grid grid-cols-3 gap-3 mb-5 mt-6">
            {chartData.map((item, idx) => {
              const dayName = dayjs(item.date).format("ddd");
              const total = item.total ?? 0;
              const active = item.active ?? 0;
              const resting = item.resting ?? 0;
              const goalReached = total >= CAL_GOAL;
              const progressPercent = Math.min((total / CAL_GOAL) * 100, 100);

              return (
                <div
                  key={idx}
                  className="bg-white shadow rounded-lg p-3 flex flex-col items-center gap-2"
                >
                  <p className="text-xs text-gray-500">{dayName}</p>
                  <div className="flex items-center gap-3">
                    <div className="relative w-16 h-16">
                      <svg className="w-full h-full" viewBox="0 0 36 36">
                        <path
                          className="stroke-gray-200"
                          strokeWidth="3"
                          fill="none"
                          d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 a15.9155 15.9155 0 1 1 0-31.831"
                        />
                        <path
                          stroke={goalReached ? "#22c55e" : "#ff6384"}
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={`${progressPercent}, 100`}
                          fill="none"
                          d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 a15.9155 15.9155 0 1 1 0-31.831"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-xs font-semibold">
                        <span>{total}</span>
                        <span className="text-gray-400">/{CAL_GOAL}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-start justify-center gap-1">
                      <p className="text-sm font-semibold text-red-500">
                        {active} kcal
                      </p>
                      <p className="text-xs text-gray-500">Active</p>
                      <p className="text-sm font-semibold text-blue-500">
                        {resting} kcal
                      </p>
                      <p className="text-xs text-gray-500">Resting</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
};

export default CaloriesData;
